<template lang="pug">
yz-dialog(v-model="showDialog" persistent scrollable max-width="640")
  yz-card
    yz-dialog-normal-header(title="View appointments" @close="onCloseDialog")
    yz-dialog-normal-content
      .pt-4
        template(v-if="appointments.length")
          div(v-for="(item, index) in appointments" :key="item.id" :class="{ 'mt-6': index > 0 }")
            .yz-heading-9.text-neutral-500.mb-2 Patient {{ index + 1 }}
            div.pa-3.rounded-lg(style="background: #F9FAFB; border: 1px solid #EEE;")
              v-row.mb-3(no-gutters align="center")
                v-col.pr-6(cols="auto" style="width: 150px;")
                  .yz-medium-body-sm.text-neutral-500 Patient details
                v-col
                  .d-flex.align-center.flex-wrap
                    yz-tooltip(:text="getPatientName(item)")
                      .yz-medium-body-sm.text-neutral-700.text-truncate(style="max-width: 245px;") {{ getPatientName(item) }}
                    v-divider.mx-2(vertical v-if="getPatientPhone(item)")
                    .yz-regular-body-sm.text-neutral-700(v-if="getPatientPhone(item)") {{ getPatientPhone(item) }}
              v-row.mb-3(no-gutters align="center")
                v-col.pr-6(cols="auto" style="width: 150px;")
                  .yz-medium-body-sm.text-neutral-500 Doctor & Service
                v-col
                  .d-flex.align-center.flex-wrap
                    yz-tooltip(v-if="getProviderName(item)" :text="getProviderName(item)")
                      .yz-medium-body-sm.text-neutral-700.text-truncate(style="max-width: 226px;") {{ getProviderName(item) }}
                    v-divider.mx-2(vertical v-if="getProviderName(item) && getServiceName(item)")
                    yz-tooltip(v-if="getServiceName(item)" :text="getServiceName(item)")
                      .yz-regular-body-sm.text-neutral-700.text-truncate(style="max-width: 226px;") {{ getServiceName(item) }}
                    div.ml-2.px-1.rounded(style="background: #F0F8FF; border: 1px solid #EEE;")
                      .yz-medium-body-xs(style="color: #3570CA;") HQ
              v-row.mb-3(no-gutters align="center")
                v-col.pr-6(cols="auto" style="width: 150px;")
                  .yz-medium-body-sm.text-neutral-500 Date & Time
                v-col
                  .d-flex.align-center
                    .yz-medium-body-sm.text-neutral-700 {{ getDate(item) }}
                    v-divider.mx-2(vertical)
                    .yz-regular-body-sm.text-neutral-700 {{ getTimeRange(item) }}
              v-row(no-gutters align="center")
                v-col.pr-6(cols="auto" style="width: 150px;")
                  .yz-medium-body-sm.text-neutral-500 Booking fee
                v-col
                  template(v-if="hasBookingFee(item)")
                    .d-flex.align-center
                      .yz-medium-body-sm.text-neutral-700 {{ formatBookingFee(item) }}
                      .ml-2.px-2.rounded(v-if="item.paid" style="background: #ECFDF3;")
                        .yz-medium-body-xs(style="color: #027A48;") Paid
                      .ml-2.px-2.rounded(v-else style="background: #FEF3F2;")
                        .yz-medium-body-xs(style="color: #B42318;") Unpaid
                  .yz-regular-body-sm.text-neutral-500.font-italic(v-else) {{ $t('no_fees_required') }}
        .yz-regular-body-sm.text-neutral-500.text-center.py-8(v-else) No appointments found.
    yz-dialog-normal-actions
      yz-btn(size="lg" text text-color="yz-neutral-700" label="Back" @click="onCloseDialog")
      yz-btn(size="lg" color="danger" label="Bulk cancel appointments" :disabled="!appointments.length" @click="onBulkCancel")
</template>

<script>
export default {
  name: 'ViewAppointmentsDialog',
  props: {
    value: { type: Boolean, default: false },
    branchId: { type: [Number, String], default: null },
    branchName: { type: String, default: '' },
    appointments: { type: Array, default: () => [] },
  },
  computed: {
    showDialog: {
      get () { return this.value },
      set (val) { this.$emit('input', val) },
    },
  },
  methods: {
    getPatientName (item) {
      return item.lead?.name || item.patient?.name || '-'
    },
    getPatientPhone (item) {
      return item.lead?.phone || item.patient?.phone || ''
    },
    getProviderName (item) {
      if (item.combo_set && item.combo_set.length) {
        return item.combo_set.map(s => s.provider?.name).filter(Boolean).join(', ')
      }
      return item.provider?.name || ''
    },
    getServiceName (item) {
      if (item.combo_set && item.combo_set.length) {
        return item.combo_set.flatMap(s => (s.services || []).map(sv => sv.name)).join(', ')
      }
      return (item.services || []).map(s => s.name).join(', ')
    },
    getDate (item) {
      const value = item.date || item.start
      if (!value) return '-'
      return this.$options.filters.humanizeDate(value) || '-'
    },
    getTimeRange (item) {
      let startVal = null
      let endVal = null
      if (Array.isArray(item.time_range) && item.time_range.length === 2) {
        [startVal, endVal] = item.time_range
      } else {
        startVal = item.start || item.time
        endVal = item.until || item.end || item.end_time
      }
      const start = startVal ? this.$options.filters.displayTime2(startVal) : ''
      const end = endVal ? this.$options.filters.displayTime2(endVal) : ''
      if (start && end) return `${start} - ${end}`
      return start || end || '-'
    },
    hasBookingFee (item) {
      const fee = item.booking_fee != null ? item.booking_fee : item.deposit
      return fee != null && Number(fee) > 0
    },
    formatBookingFee (item) {
      const fee = item.booking_fee != null ? item.booking_fee : item.deposit
      const value = Number(fee) / Number(this.$currency.multiplier)
      return `${this.$currency.symbol}${value.toFixed(this.$currency.exponent)}`
    },
    onBulkCancel () {
      if (!this.appointments.length) return
      this.$emit('request-bulk-cancel', {
        branchId: this.branchId,
        branchName: this.branchName,
        appointmentIds: this.appointments.map(a => a.id),
      })
      this.showDialog = false
    },
    onCloseDialog () {
      this.showDialog = false
    },
  },
}
</script>
